import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import './ExportModal.css';

const ExportModal = ({ properties, filters, onClose, onFetchAll }) => {
  const [exportFormat, setExportFormat] = useState('csv');
  const [includeOptions, setIncludeOptions] = useState({
    basicInfo: true,
    financials: true,
    location: true,
    ownership: false,
    vacancy: true,
    demographics: true,
    eviction: true,
    calculations: true
  });
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      let dataToExport = properties;

      // If onFetchAll is provided, fetch full dataset
      if (onFetchAll) {
        console.log('Fetching full dataset for export...');
        dataToExport = await onFetchAll();
      }

      if (exportFormat === 'csv') {
        exportToCSV(dataToExport);
      } else if (exportFormat === 'pdf') {
        exportToPDF(dataToExport);
      } else if (exportFormat === 'excel') {
        exportToExcel(dataToExport); // Uses exportToCSV internally currently
      }

      setTimeout(() => {
        setIsExporting(false);
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Export error:', error);
      setIsExporting(false);
    }
  };

  const exportToCSV = (data) => {
    const headers = [];
    const rows = [];

    const propertiesToExport = data || properties;

    // Build headers based on selected options
    if (includeOptions.basicInfo) {
      headers.push('Property Name', 'Address', 'Units', 'Year Built', 'Building Class', 'Property Type', 'Style');
    }
    if (includeOptions.financials) {
      headers.push('Rent/SF', 'Total RBA', 'Tract Income Level', 'Median Income', 'MSA Median Income', 'Income % of MSA', 'Income Category');
    }
    if (includeOptions.location) {
      headers.push('City', 'State', 'Zip', 'County', 'Submarket', 'Latitude', 'Longitude');
    }
    if (includeOptions.ownership) {
      headers.push('Owner Name', 'Property Manager');
    }
    if (includeOptions.vacancy) {
      headers.push('Vacancy %');
    }
    if (includeOptions.demographics) {
      headers.push('Population', 'Median Age', 'Median Home Value', 'Median Rent', 'Minority %', 'LMI %', 'LMI Population');
    }
    if (includeOptions.eviction) {
      headers.push('Eviction Filing Rate', 'Eviction Filings', 'Eviction Threatened', 'Eviction Judgements', 'Eviction Year');
    }
    if (includeOptions.calculations) {
      headers.push('Opportunity Score', 'Cap Rate', 'Rent Gap %');
    }

    // Build data rows
    propertiesToExport.forEach(property => {
      const row = [];

      if (includeOptions.basicInfo) {
        row.push(
          property.propertyName || '',
          property.propertyAddress || '',
          property.numberOfUnits || 0,
          property.yearBuilt || '',
          property.buildingClass || '',
          property.propertyType || '',
          property.style || ''
        );
      }
      if (includeOptions.financials) {
        row.push(
          property.rentPerSF || 0,
          property.rba || 0,
          property.tract_income_level || property.incomeLevel || '',
          property.medianIncome || 0,
          property.msaMedianIncome || 0,
          property.incomePercent || 0,
          property.incomeCategory || ''
        );
      }
      if (includeOptions.location) {
        row.push(
          property.city || '',
          property.state || '',
          property.zipCode || '',
          property.county || '',
          property.submarket || '',
          property.latitude || '',
          property.longitude || ''
        );
      }
      if (includeOptions.ownership) {
        row.push(
          property.ownerName || '',
          property.propertyManagerName || ''
        );
      }
      if (includeOptions.vacancy) {
        row.push(
          property.vacancyPercent || 0
        );
      }
      if (includeOptions.demographics) {
        row.push(
          property.population || 0,
          property.medianAge || 0,
          property.medianHomeValue || 0,
          property.medianRent || 0,
          property.minorityPercent || 0,
          property.lmiPercent || 0,
          property.lmiPopulation || 0
        );
      }
      if (includeOptions.eviction) {
        row.push(
          property.evictionFilingRate || 0,
          property.evictionFilings || 0,
          property.evictionThreatened || 0,
          property.evictionJudgements || 0,
          property.evictionYear || ''
        );
      }
      if (includeOptions.calculations) {
        row.push(
          calculateOpportunityScore(property),
          calculateCapRate(property),
          calculateRentGap(property)
        );
      }

      rows.push(row);
    });

    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Yieldmap_properties_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = (data) => {
    const propertiesToExport = data || properties;
    const doc = new jsPDF('p', 'mm', 'a4');

    // Color scheme
    const primaryColor = [79, 70, 229];
    const darkGray = [31, 41, 55];
    const mediumGray = [107, 114, 128];
    const lightGray = [243, 244, 246];

    // Helper function to add a new page with header
    const addNewPage = () => {
      doc.addPage();
      doc.setFontSize(8);
      doc.setTextColor(...mediumGray);
      doc.text('Columbus Property Report', 10, 10);
      doc.text(`Page ${doc.internal.getNumberOfPages()}`, 190, 10, { align: 'right' });
      return 20; // Return starting Y position for content
    };

    // ===== TITLE PAGE =====
    // Add header background
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 210, 60, 'F');

    // Add title
    doc.setFontSize(28);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.text('COLUMBUS', 105, 25, { align: 'center' });
    doc.setFontSize(24);
    doc.setFont(undefined, 'normal');
    doc.text('Property Analysis Report', 105, 35, { align: 'center' });

    // Add generation date
    doc.setFontSize(11);
    doc.text(`Generated on ${new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })}`, 105, 45, { align: 'center' });

    // ===== EXECUTIVE SUMMARY BOX =====
    let yPosition = 75;

    // Summary box
    doc.setFillColor(...lightGray);
    doc.roundedRect(15, yPosition - 5, 180, 40, 3, 3, 'F');

    doc.setFontSize(14);
    doc.setTextColor(...darkGray);
    doc.setFont(undefined, 'bold');
    doc.text('Executive Summary', 20, yPosition + 5);

    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    const summaryStats = [
      { label: 'Total Properties Analyzed:', value: propertiesToExport.length.toLocaleString() },
      { label: 'Total Units:', value: propertiesToExport.reduce((sum, p) => sum + (p.numberOfUnits || 0), 0).toLocaleString() },
      { label: 'Average Units per Property:', value: Math.round(propertiesToExport.reduce((sum, p) => sum + (p.numberOfUnits || 0), 0) / propertiesToExport.length).toLocaleString() },
      { label: 'Average Vacancy Rate:', value: `${(propertiesToExport.reduce((sum, p) => sum + (p.vacancyPercent || 0), 0) / propertiesToExport.length).toFixed(1)}%` }
    ];

    summaryStats.forEach((stat, index) => {
      const x = index % 2 === 0 ? 25 : 110;
      const y = yPosition + 15 + Math.floor(index / 2) * 10;
      doc.setFont(undefined, 'normal');
      doc.text(stat.label, x, y);
      doc.setFont(undefined, 'bold');
      doc.text(stat.value, x + 50, y);
    });

    yPosition = 125;

    // ===== SEARCH CRITERIA / FILTERS SECTION =====
    doc.setFontSize(14);
    doc.setTextColor(...darkGray);
    doc.setFont(undefined, 'bold');
    doc.text('Search Criteria & Filters Applied', 20, yPosition);

    yPosition += 10;

    // Use passed filters or default values
    const filterData = filters || {
      location: 'Columbus, OH',
      propertyType: 'Multifamily',
      minUnits: '50',
      maxUnits: '500',
      yearBuiltRange: '1980-2020',
      submarkets: ['Downtown', 'Short North', 'Clintonville', 'German Village'],
      vacancyRange: '5-20%',
      rentPerSFRange: '$1.00-$2.50'
    };

    // Filter box
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...mediumGray);
    doc.setLineWidth(0.5);
    doc.roundedRect(15, yPosition - 5, 180, 50, 3, 3, 'D');

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...mediumGray);

    const filterText = [
      `• Location: ${filterData.location || 'Columbus, OH'}`,
      `• Property Type: ${filterData.propertyType || 'All Types'}`,
      `• Unit Range: ${filterData.minUnits || '0'} - ${filterData.maxUnits || 'Unlimited'} units`,
      `• Year Built: ${filterData.yearBuiltRange || 'All Years'}`,
      `• Vacancy Range: ${filterData.vacancyRange || 'All'}`,
      `• Rent/SF Range: ${filterData.rentPerSFRange || 'All'}`,
      `• Submarkets: ${filterData.submarkets?.join(', ') || 'All Submarkets'}`
    ];

    filterText.forEach((text, index) => {
      const x = index % 2 === 0 ? 20 : 105;
      const y = yPosition + 5 + Math.floor(index / 2) * 8;
      doc.text(text, x, y);
    });

    // ===== PROPERTY DETAILS TABLE =====
    // Start new page for property details
    doc.addPage();
    yPosition = 20;

    // Page header
    doc.setFontSize(16);
    doc.setTextColor(...darkGray);
    doc.setFont(undefined, 'bold');
    doc.text('Property Details', 20, yPosition);

    yPosition += 10;

    // Prepare table data
    const tableHeaders = [];
    const tableData = [];

    // Build headers based on selected options
    if (includeOptions.basicInfo) {
      tableHeaders.push('Property Name', 'Units', 'Year', 'Class');
    }
    if (includeOptions.financials) {
      tableHeaders.push('Rent/SF', 'Tract Income', 'Med. Income');
    }
    if (includeOptions.vacancy) {
      tableHeaders.push('Vacancy');
    }
    if (includeOptions.demographics) {
      tableHeaders.push('Population', 'Minority %');
    }
    if (includeOptions.eviction) {
      tableHeaders.push('Filing Rate', 'Filings');
    }
    if (includeOptions.location) {
      tableHeaders.push('Submarket');
    }
    if (includeOptions.calculations) {
      tableHeaders.push('Score');
    }

    // Build data rows
    // Build data rows
    propertiesToExport.forEach(property => {
      const row = [];

      if (includeOptions.basicInfo) {
        row.push(
          property.propertyName || property.propertyAddress?.substring(0, 25) || 'N/A',
          property.numberOfUnits || 0,
          property.yearBuilt || 'N/A',
          property.buildingClass || 'N/A'
        );
      }
      if (includeOptions.financials) {
        row.push(
          `$${property.rentPerSF?.toFixed(2) || '0.00'}`,
          property.tract_income_level || property.incomeLevel || 'N/A',
          property.medianIncome ? `$${property.medianIncome.toLocaleString()}` : 'N/A'
        );
      }
      if (includeOptions.vacancy) {
        row.push(`${property.vacancyPercent?.toFixed(1) || 0}%`);
      }
      if (includeOptions.demographics) {
        row.push(
          property.population?.toLocaleString() || 'N/A',
          property.minorityPercent ? `${property.minorityPercent.toFixed(1)}%` : 'N/A'
        );
      }
      if (includeOptions.eviction) {
        row.push(
          property.evictionFilingRate ? `${property.evictionFilingRate.toFixed(2)}%` : 'N/A',
          property.evictionFilings?.toLocaleString() || '0'
        );
      }
      if (includeOptions.location) {
        row.push(property.submarket || 'N/A');
      }
      if (includeOptions.calculations) {
        row.push(calculateOpportunityScore(property).toString());
      }

      tableData.push(row);
    });

    // Add table using autoTable
    doc.autoTable({
      head: [tableHeaders],
      body: tableData,
      startY: yPosition,
      theme: 'striped',
      headStyles: {
        fillColor: primaryColor,
        textColor: 255,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 8,
        cellPadding: 2,
        textColor: darkGray
      },
      alternateRowStyles: {
        fillColor: [248, 249, 250]
      },
      columnStyles: {
        0: { cellWidth: 'auto', halign: 'left' },
        1: { cellWidth: 20, halign: 'center' },
        2: { cellWidth: 20, halign: 'center' },
        3: { cellWidth: 20, halign: 'center' }
      },
      margin: { left: 15, right: 15 },
      didDrawPage: function (data) {
        // Add page header and footer on each page
        doc.setFontSize(8);
        doc.setTextColor(...mediumGray);
        doc.text('Columbus Property Report', 15, 10);
        doc.text(`Page ${data.pageNumber}`, 195, 10, { align: 'right' });

        // Footer
        doc.setLineWidth(0.5);
        doc.setDrawColor(...lightGray);
        doc.line(15, 285, 195, 285);
        doc.setFontSize(8);
        doc.text('Confidential Property Analysis Report', 105, 290, { align: 'center' });
      }
    });

    // ===== MARKET INSIGHTS PAGE =====
    doc.addPage();
    yPosition = 20;

    doc.setFontSize(16);
    doc.setTextColor(...darkGray);
    doc.setFont(undefined, 'bold');
    doc.text('Market Insights & Analysis', 20, yPosition);

    yPosition += 15;

    // Calculate market statistics
    const avgRentPerSF = propertiesToExport.reduce((sum, p) => sum + (p.rentPerSF || 0), 0) / propertiesToExport.length;
    const avgVacancy = propertiesToExport.reduce((sum, p) => sum + (p.vacancyPercent || 0), 0) / propertiesToExport.length;
    const propertiesByClass = propertiesToExport.reduce((acc, p) => {
      acc[p.buildingClass || 'Unknown'] = (acc[p.buildingClass || 'Unknown'] || 0) + 1;
      return acc;
    }, {});

    // Key Metrics Box
    doc.setFillColor(...lightGray);
    doc.roundedRect(15, yPosition, 180, 60, 3, 3, 'F');

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...darkGray);
    doc.text('Key Market Metrics', 20, yPosition + 10);

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    const metrics = [
      { label: 'Average Rent per SF:', value: `$${avgRentPerSF.toFixed(2)}` },
      { label: 'Average Vacancy Rate:', value: `${avgVacancy.toFixed(1)}%` },
      { label: 'Properties Above 15% Vacancy:', value: propertiesToExport.filter(p => p.vacancyPercent > 15).length.toString() },
      { label: 'Properties Built Before 1990:', value: propertiesToExport.filter(p => p.yearBuilt < 1990).length.toString() },
      { label: 'Total Square Footage:', value: propertiesToExport.reduce((sum, p) => sum + (p.rba || 0), 0).toLocaleString() },
      { label: 'Class A Properties:', value: (propertiesByClass['A'] || 0).toString() }
    ];

    metrics.forEach((metric, index) => {
      const x = index % 2 === 0 ? 25 : 110;
      const y = yPosition + 25 + Math.floor(index / 2) * 10;
      doc.setFont(undefined, 'normal');
      doc.text(metric.label, x, y);
      doc.setFont(undefined, 'bold');
      doc.text(metric.value, x + 50, y);
    });

    yPosition += 70;

    // Top Opportunities Section
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text('Top Investment Opportunities', 20, yPosition);

    yPosition += 10;

    // Sort properties by opportunity score
    const topProperties = propertiesToExport
      .map(p => ({ ...p, score: calculateOpportunityScore(p) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5);

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    topProperties.forEach((property, index) => {
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(...mediumGray);
      doc.setLineWidth(0.3);
      doc.roundedRect(15, yPosition - 3, 180, 18, 2, 2, 'FD');

      doc.setTextColor(...primaryColor);
      doc.setFont(undefined, 'bold');
      doc.text(`${index + 1}.`, 20, yPosition + 3);

      doc.setTextColor(...darkGray);
      doc.text(property.propertyName || property.propertyAddress || 'N/A', 30, yPosition + 3);

      doc.setFont(undefined, 'normal');
      doc.setTextColor(...mediumGray);
      doc.text(`${property.numberOfUnits || 0} units | ${property.vacancyPercent || 0}% vacancy | Score: ${property.score}`, 30, yPosition + 10);

      yPosition += 22;
    });

    // ===== FOOTER ON LAST PAGE =====
    const pageCount = doc.internal.getNumberOfPages();
    doc.setPage(pageCount);

    doc.setLineWidth(0.5);
    doc.setDrawColor(...mediumGray);
    doc.line(15, 270, 195, 270);

    doc.setFontSize(9);
    doc.setTextColor(...mediumGray);
    doc.text('This report contains confidential and proprietary information.', 105, 275, { align: 'center' });
    doc.text(`© ${new Date().getFullYear()} Columbus Property Analysis. All rights reserved.`, 105, 280, { align: 'center' });

    // Save the PDF
    doc.save(`Yieldmap_properties_report_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = (data) => {
    exportToCSV(data);
  };

  const calculateOpportunityScore = (property) => {
    let score = 0;
    if (property.vacancyPercent > 10) score += 20;
    if (property.yearBuilt < 1990) score += 15;
    if (property.rentPerSF < 1.5) score += 25;
    if (property.numberOfUnits > 100) score += 20;
    return score;
  };

  const calculateCapRate = (property) => {
    if (!property.rentPerSF || !property.rba) return 0;
    const annualRent = property.rentPerSF * property.rba * 12;
    const estimatedValue = property.numberOfUnits * 75000;
    const noi = annualRent * 0.6;
    return ((noi / estimatedValue) * 100).toFixed(2);
  };

  const calculateRentGap = (property) => {
    const marketAvg = 1.8;
    if (!property.rentPerSF) return 0;
    return (((marketAvg - property.rentPerSF) / marketAvg) * 100).toFixed(2);
  };

  function formatOptionLabel(key) {
    const labels = {
      basicInfo: 'Basic Information',
      financials: 'Financial & Income Data',
      location: 'Location Details',
      ownership: 'Ownership Info',
      vacancy: 'Vacancy Data',
      demographics: 'Census Demographics',
      eviction: 'Eviction Data',
      calculations: 'Calculated Metrics'
    };
    return labels[key] || key;
  }

  return (
    <div className="export-modal-overlay">
      <div className="export-modal">
        <div className="modal-header">
          <h2>Export Properties</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="export-section">
            <h3>Export Format</h3>
            <div className="format-options">
              <label className={`format-option ${exportFormat === 'csv' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="csv"
                  checked={exportFormat === 'csv'}
                  onChange={(e) => setExportFormat(e.target.value)}
                />
                <div className="format-info">
                  <div className="format-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                      <path d="M3 9H21M9 9V21M15 9V21" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </div>
                  <div>
                    <div className="format-name">CSV</div>
                    <div className="format-desc">Excel compatible</div>
                  </div>
                </div>
              </label>

              <label className={`format-option ${exportFormat === 'pdf' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="pdf"
                  checked={exportFormat === 'pdf'}
                  onChange={(e) => setExportFormat(e.target.value)}
                />
                <div className="format-info">
                  <div className="format-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6C5 2 4 3 4 4V20C4 21 5 22 6 22H18C19 22 20 21 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" />
                      <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </div>
                  <div>
                    <div className="format-name">PDF Report</div>
                    <div className="format-desc">Formatted document</div>
                  </div>
                </div>
              </label>

              <label className={`format-option ${exportFormat === 'excel' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  value="excel"
                  checked={exportFormat === 'excel'}
                  onChange={(e) => setExportFormat(e.target.value)}
                />
                <div className="format-info">
                  <div className="format-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                      <path d="M8 12L16 12M12 8L12 16" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </div>
                  <div>
                    <div className="format-name">Excel</div>
                    <div className="format-desc">Spreadsheet format</div>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="export-section">
            <h3>Include Data</h3>
            <div className="include-options">
              {Object.entries(includeOptions).map(([key, value]) => (
                <label key={key} className="include-option">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => setIncludeOptions(prev => ({
                      ...prev,
                      [key]: e.target.checked
                    }))}
                  />
                  <span>{formatOptionLabel(key)}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="export-summary">
            <div className="summary-item">
              <span>Properties to export:</span>
              <strong>{properties.length}</strong>
            </div>
            <div className="summary-item">
              <span>Total units:</span>
              <strong>{properties.reduce((sum, p) => sum + (p.numberOfUnits || 0), 0).toLocaleString()}</strong>
            </div>
            <div className="summary-item">
              <span>File format:</span>
              <strong>{exportFormat.toUpperCase()}</strong>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-export"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <span className="spinner-small"></span>
                Exporting...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 10L8 2M8 10L5 7M8 10L11 7M2 14H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                Export {properties.length} Properties
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;